import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Base_URL } from "../../Client/apiURL";
import { useMockTestScore } from "../../context/MockTestScoreContext";
import AnalyticsForMtScore from "./AnalyticsForMtScore";
import EndTestScoreCard from "./EndTestScoreCard";
import { fetchMockTestScore, splitCategories } from "./helperFunctions";
import { testData } from "./data";
import {
  calculateQuestionTypeScore,
  getQuestionTypeRawScore,
} from "./questionTypeScoring";

const categoryColors = {
  Speaking: "#20a7c2",
  Writing: "#d9485f",
  Reading: "#8b6655",
  Listening: "#68708d",
};

const ViewScoreQuestionType = () => {
  const { id, userId, mockTestAttemptedId } = useParams();
  const attemptId = id || mockTestAttemptedId;
  const location = useLocation();
  const navigate = useNavigate();
  const { scores } = useMockTestScore();
  const [metadata, setMetadata] = useState(null);
  const [results, setResults] = useState([]);
  const [counts, setCounts] = useState(null);
  const [listening, setListening] = useState([]);
  const [reading, setReading] = useState([]);
  const [writing, setWriting] = useState([]);
  const [speaking, setSpeaking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const isSharedView = Boolean(userId && mockTestAttemptedId);
      const isAdminView = new URLSearchParams(location.search).get("source") === "admin";
      const metadataRequest = isSharedView
        ? Promise.resolve(null)
        : axios.get(
            `${Base_URL}/app/users/mock-test-attempts/question-type-metadata`,
            { params: { mock_test_attempted_id: attemptId }, withCredentials: true }
          );
      const scoreUrl = isSharedView
        ? isAdminView
          ? `${Base_URL}/app/admin/mock-tests/get-score?user_id=${userId}&mock_test_attempted_id=${attemptId}`
          : `${Base_URL}/app/users/teacher-access/students/${userId}/pte/score?mock_test_attempted_id=${attemptId}`
        : `${Base_URL}/app/users/mock-test-attempts/get-score?mock_test_attempted_id=${attemptId}`;
      const scoreRequest = fetchMockTestScore({
        url: scoreUrl,
        setIsLoading: () => {},
        setMockTestsResult: setResults,
        withCredentials: !isAdminView,
      });

      try {
        const [metadataResponse, scoreResponse] = await Promise.all([metadataRequest, scoreRequest]);
        if ((!isSharedView && metadataResponse.data.responseCode !== 200) || !scoreResponse.success) {
          throw new Error(scoreResponse.message || metadataResponse?.data?.message);
        }
        const firstResult = scoreResponse.data[0];
        setMetadata(isSharedView ? {
          MockTestAttemptId: Number(attemptId),
          MockTestName: firstResult?.MockTestName,
          QuestionTypeName: firstResult?.UserResponse?.SubCategory,
          Category: firstResult?.UserResponse?.Category,
        } : metadataResponse.data.response);
        splitCategories(
          scoreResponse.data,
          setCounts,
          setListening,
          setReading,
          setWriting,
          setSpeaking
        );
      } catch (loadError) {
        setError(loadError.message || "Unable to load this score report");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [attemptId, userId, location.search]);

  const categoryKey = (metadata?.Category || "").toLowerCase();
  const categoryResults = useMemo(() => ({
    speaking,
    writing,
    reading,
    listening,
  })[categoryKey] || results, [categoryKey, listening, reading, writing, speaking, results]);
  const score = calculateQuestionTypeScore(scores, metadata?.QuestionTypeName);
  const raw = getQuestionTypeRawScore(scores, metadata?.QuestionTypeName);
  const color = categoryColors[metadata?.Category] || "#6d42d8";

  if (loading) {
    return <Box sx={{ minHeight: "75vh", display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
  }

  if (error || !metadata) {
    return (
      <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center", p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Typography color="error">{error || "Score report not found"}</Typography>
          <Button onClick={() => navigate("/mt-score")}>Back to results</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "100vh", pt: { xs: 3, md: 12 }, px: { xs: 2, md: 5 }, pb: 4 }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => userId ? window.close() : navigate("/mt-score")} sx={{ mb: 2, textTransform: "none" }}>
        Results
      </Button>
      <Box sx={{ bgcolor: "white", p: { xs: 2, md: 3 }, boxShadow: "0 4px 12px rgba(51,51,51,0.08)" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">Question-Type Mock Test</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{metadata.MockTestName}</Typography>
            <Typography sx={{ color, mt: 0.5 }}>{metadata.QuestionTypeName} · {metadata.Category}</Typography>
          </Box>
          <Stack direction="row" spacing={3} alignItems="center">
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h3" sx={{ color, fontWeight: 700 }}>{score}</Typography>
              <Typography color="text.secondary">out of 90</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Raw marks</Typography>
              <Typography sx={{ fontWeight: 700 }}>{raw.obtained.toFixed(1)} / {raw.available.toFixed(1)}</Typography>
              <Typography variant="body2" color="text.secondary">{categoryResults.length} questions</Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>
        {testData[categoryKey] && (
          <EndTestScoreCard
            data={testData[categoryKey][0]}
            tests={categoryResults}
            type={categoryKey}
            explanationContext={{ mockTestAttemptedId: attemptId, studentUserId: userId || undefined }}
          />
        )}
      </Box>
      <AnalyticsForMtScore counts={counts} seen={false} />
    </Box>
  );
};

export default ViewScoreQuestionType;
